/**
 * Agenda Builder — find the "Questions" column in an FDD databook.
 *
 * The canonical layout is the one written by `server/databook/builder.ts`:
 *   - Tabs named "IS - Monthly" and "BS - Monthly".
 *   - A header cell whose value is exactly "Questions" sits in the column
 *     where each row's trending questions are concatenated with `\n\n`.
 *   - See builder.ts:841, 2176, 1868, 3199 for where the writes happen.
 *
 * Detection runs per recognized tab and returns:
 *   - column number (1-based) and header row number (1-based) of the
 *     Questions column, or null if nothing matched.
 *   - confidence: "exact" (literal `Questions` cell hit), "heuristic"
 *     (header label matched a fuzzy pattern), "unresolved" (need user input).
 *
 * AI-fallback step is intentionally out of scope for v1 — exact-match wins
 * for any databook this codebase produced, and the UI exposes a per-tab
 * column override for foreign databooks.
 */

import * as XLSX from "xlsx";
import type { AgendaDetectionConfidence } from "./storage";

/**
 * Canonical tab keys we use everywhere downstream (storage, prompts,
 * assignment rules). The actual sheet name in any given workbook is
 * resolved by `findSheetForTab` — fuzzy match so minor variations don't
 * break detection (e.g., extra whitespace, different casing, "Monthly IS"
 * reordering, "Income Statement - Monthly", etc.).
 */
export const RECOGNIZED_TABS = ["IS - Monthly", "BS - Monthly"] as const;
export type RecognizedTab = (typeof RECOGNIZED_TABS)[number];

/**
 * Try to find the sheet that corresponds to a canonical tab. Strategy:
 *   1. Exact case-sensitive match (fastest, covers the canonical case).
 *   2. Case-insensitive + whitespace-collapsed match.
 *   3. Fuzzy: must contain "monthly" AND the IS/BS marker
 *      ("is" / "income statement" / "p&l" for IS; "bs" / "balance sheet" for BS).
 * Returns null if no candidate sheet matched.
 */
export function findSheetForTab(workbook: XLSX.WorkBook, tab: RecognizedTab): string | null {
  const names = workbook.SheetNames;
  if (names.includes(tab)) return tab;

  const norm = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();
  const target = norm(tab);
  const exactNoCase = names.find((n) => norm(n) === target);
  if (exactNoCase) return exactNoCase;

  const isIs = tab === "IS - Monthly";
  const fuzzy = names.find((n) => {
    const nn = norm(n);
    if (!/\bmonthly\b/.test(nn)) return false;
    if (isIs) {
      return /\b(is|i\.?s\.?|income\s+statement|p\s*&\s*l|profit\s+(?:and|&)\s+loss)\b/.test(nn);
    }
    return /\b(bs|b\.?s\.?|balance\s+sheet)\b/.test(nn);
  });
  return fuzzy ?? null;
}

/** Fuzzy header labels we try when an exact "Questions" cell isn't found. */
const HEURISTIC_PATTERNS: RegExp[] = [
  /^questions?$/i,
  /^mgmt\s*q'?s?$/i,
  /^trending\s+questions?$/i,
  /^follow.?ups?$/i,
  /^open\s*items?$/i,
];

/** Cell keys in a SheetJS worksheet look like "A1", "AB42" — non-cell metadata
 * keys start with "!" (e.g., "!ref", "!merges"). */
const CELL_KEY_RE = /^[A-Z]+\d+$/;

/** Does a cell value normalize to a "Questions" header? Handles plain
 * strings, rich text, and stray BOM / zero-width whitespace. */
function isQuestionsHeader(value: unknown): boolean {
  if (value == null) return false;
  let str = "";
  if (typeof value === "string") str = value;
  else if (typeof value === "number" || typeof value === "boolean") str = String(value);
  else if (typeof value === "object") {
    const v: any = value;
    if (typeof v.text === "string") str = v.text;
    else if (Array.isArray(v.richText)) str = v.richText.map((r: any) => r?.text ?? "").join("");
    else str = String(value);
  } else {
    str = String(value);
  }
  const normalized = str
    .toLowerCase()
    .replace(/[​-‍﻿]/g, "") // zero-width / BOM
    .replace(/\s+/g, " ")
    .trim();
  return normalized === "questions" || normalized === "question";
}

export type TabDetection = {
  tab: RecognizedTab;
  /** Exists in the workbook (might be empty / lacking a Questions column). */
  present: boolean;
  /**
   * Whichever sheet name in the workbook ended up matching the canonical
   * tab — may differ from `tab` if the workbook used a variation like
   * "Monthly IS" or "Income Statement - Monthly".
   */
  actualSheetName: string | null;
  /** 1-based column number, or null if nothing matched. */
  questionCol: number | null;
  /** 1-based column number of the Agenda routing column, or null if absent. */
  agendaCol: number | null;
  /** 1-based column number of the IRL routing column, or null if absent. */
  irlCol: number | null;
  /** 1-based row number where the column header was found. */
  headerRow: number;
  confidence: AgendaDetectionConfidence;
};

export type DetectionResult = {
  tabs: TabDetection[];
  /** Worst-case confidence across all present tabs (drives the modal copy). */
  overallConfidence: AgendaDetectionConfidence;
};

/**
 * Run detection across all recognized tabs in a workbook.
 */
export function detectQuestionColumns(workbook: XLSX.WorkBook): DetectionResult {
  const tabs: TabDetection[] = RECOGNIZED_TABS.map((name) => detectTab(workbook, name));
  const overall = aggregateConfidence(tabs);
  return { tabs, overallConfidence: overall };
}

function detectTab(workbook: XLSX.WorkBook, tab: RecognizedTab): TabDetection {
  const actualSheetName = findSheetForTab(workbook, tab);
  if (!actualSheetName) {
    return { tab, present: false, actualSheetName: null, questionCol: null, agendaCol: null, irlCol: null, headerRow: 1, confidence: "unresolved" };
  }
  const sheet = workbook.Sheets[actualSheetName];
  if (!sheet) {
    return { tab, present: false, actualSheetName: null, questionCol: null, agendaCol: null, irlCol: null, headerRow: 1, confidence: "unresolved" };
  }

  const range = sheet["!ref"] ? XLSX.utils.decode_range(sheet["!ref"]) : null;
  if (!range) {
    return { tab, present: true, actualSheetName, questionCol: null, agendaCol: null, irlCol: null, headerRow: 1, confidence: "unresolved" };
  }

  // 1) Exact-match — scan EVERY cell in the sheet (not bounded by !ref,
  //    which ExcelJS sometimes reports tighter than the actual data) looking
  //    for any cell whose value normalizes to "questions". The builder always
  //    writes that header literally (server/databook/builder.ts:1868), so
  //    this is the canonical-case fast path.
  for (const key of Object.keys(sheet)) {
    if (!CELL_KEY_RE.test(key)) continue;
    const cell = sheet[key];
    if (!cell) continue;
    if (!isQuestionsHeader(cell.v)) continue;
    const { c, r } = XLSX.utils.decode_cell(key);
    const { agendaCol, irlCol } = findRoutingCols(sheet, r, c);
    return {
      tab,
      present: true,
      actualSheetName,
      questionCol: c + 1, // 1-based for our pin metadata
      agendaCol,
      irlCol,
      headerRow: r + 1,
      confidence: "exact",
    };
  }

  // 2) Heuristic — find a likely header row, then look for fuzzy header matches.
  const headerRow = findHeaderRow(sheet, range);
  if (headerRow != null) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const ref = XLSX.utils.encode_cell({ r: headerRow, c });
      const cell = sheet[ref];
      if (!cell || cell.v == null) continue;
      const val = String(cell.v).trim();
      if (HEURISTIC_PATTERNS.some((p) => p.test(val))) {
        const { agendaCol, irlCol } = findRoutingCols(sheet, headerRow, c);
        return {
          tab,
          present: true,
          actualSheetName,
          questionCol: c + 1,
          agendaCol,
          irlCol,
          headerRow: headerRow + 1,
          confidence: "heuristic",
        };
      }
    }
  }

  // 3) Nothing matched — leave it unresolved for the user to pick.
  return {
    tab,
    present: true,
    actualSheetName,
    questionCol: null,
    agendaCol: null,
    irlCol: null,
    headerRow: (headerRow ?? 0) + 1,
    confidence: "unresolved",
  };
}

/**
 * Scan cells to the right of the Questions column on the same header row
 * looking for "Agenda" and "IRL" header labels. Returns 1-based column
 * numbers or null if not found. Looks up to 10 columns right to tolerate
 * a Comments column sitting between Questions and the routing columns.
 */
function findRoutingCols(sheet: XLSX.WorkSheet, headerRow0: number, questionCol0: number): { agendaCol: number | null; irlCol: number | null } {
  let agendaCol: number | null = null;
  let irlCol: number | null = null;
  for (let offset = 1; offset <= 10; offset++) {
    const ref = XLSX.utils.encode_cell({ r: headerRow0, c: questionCol0 + offset });
    const cell = sheet[ref];
    if (!cell || cell.v == null) continue;
    const val = String(cell.v).trim().toLowerCase();
    if (val === "agenda") agendaCol = questionCol0 + offset + 1;
    else if (val === "irl") irlCol = questionCol0 + offset + 1;
    if (agendaCol !== null && irlCol !== null) break;
  }
  return { agendaCol, irlCol };
}

/**
 * "Likely header row" = the first row in the first ~30 rows that has at
 * least 3 non-empty cells and at least one cell whose value looks like a
 * column label (short string, no numbers or formulas dominating).
 */
function findHeaderRow(sheet: XLSX.WorkSheet, range: XLSX.Range): number | null {
  const limit = Math.min(range.e.r, range.s.r + 30);
  for (let r = range.s.r; r <= limit; r++) {
    let nonEmpty = 0;
    let stringCells = 0;
    for (let c = range.s.c; c <= range.e.c; c++) {
      const ref = XLSX.utils.encode_cell({ r, c });
      const cell = sheet[ref];
      if (!cell || cell.v == null || cell.v === "") continue;
      nonEmpty++;
      if (cell.t === "s") stringCells++;
    }
    if (nonEmpty >= 3 && stringCells >= 2) return r;
  }
  return null;
}

function aggregateConfidence(tabs: TabDetection[]): AgendaDetectionConfidence {
  const present = tabs.filter((t) => t.present);
  if (present.length === 0) return "unresolved";
  // Worst-case: a single unresolved tab drags the overall down.
  if (present.some((t) => t.confidence === "unresolved" || t.questionCol == null)) {
    return "unresolved";
  }
  if (present.some((t) => t.confidence === "heuristic")) return "heuristic";
  if (present.every((t) => t.confidence === "exact")) return "exact";
  return present.some((t) => t.confidence === "user") ? "user" : "heuristic";
}

/**
 * Convenience: extract the header-row labels for a tab so the picker UI can
 * show columns by their displayed name when the user overrides the choice.
 */
export function getHeaderLabels(workbook: XLSX.WorkBook, tab: RecognizedTab, headerRow1Based: number): { col: number; label: string }[] {
  const sheet = workbook.Sheets[tab];
  if (!sheet || !sheet["!ref"]) return [];
  const range = XLSX.utils.decode_range(sheet["!ref"]!);
  const r = headerRow1Based - 1;
  const out: { col: number; label: string }[] = [];
  for (let c = range.s.c; c <= range.e.c; c++) {
    const ref = XLSX.utils.encode_cell({ r, c });
    const cell = sheet[ref];
    const v = cell?.v;
    if (v == null || v === "") continue;
    out.push({ col: c + 1, label: String(v).trim() });
  }
  return out;
}
